/** DEMO ONLY — View 2: configure a product's prices against the bands. */
import React, { useState, useMemo } from "react";
import {
  Typography,
  Paper,
  Box,
  Grid,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  InputAdornment,
  Button,
  Snackbar,
  Divider,
} from "@mui/material";
import BandSetEditor from "../BandSetEditor";
import { useDemo } from "../demoStore";
import { SPORTS, ITEMS, FABRICS, BAND_PRESETS } from "../demoData";
import { formatCents } from "../resolver";

const centsToInput = (c) => (c === undefined || c === null ? "" : c / 100);

export default function ConfigureProductView() {
  const { state, dispatch } = useDemo();
  const [sportId, setSportId] = useState(1);
  const [itemId, setItemId] = useState(1);
  const [fabricId, setFabricId] = useState(1);
  const [useCustom, setUseCustom] = useState(false);
  const [customBands, setCustomBands] = useState(BAND_PRESETS["Ice Hockey"]);
  const [values, setValues] = useState({}); // label -> dollar string
  const [fob, setFob] = useState("");
  const [toast, setToast] = useState("");

  const items = ITEMS.filter((i) => i.sportId === sportId);

  // find an existing demo price for this combo
  const existing = useMemo(
    () =>
      state.prices.find(
        (p) =>
          p.sportId === sportId && p.itemId === itemId && p.fabricId === fabricId
      ),
    [state.prices, sportId, itemId, fabricId]
  );

  const loadExisting = () => {
    if (!existing) {
      setUseCustom(false);
      setValues({});
      setFob("");
      setToast("No demo price for this combo yet — enter one below.");
      return;
    }
    const isCustom = !!existing.presetName;
    setUseCustom(isCustom);
    if (isCustom) setCustomBands(BAND_PRESETS[existing.presetName]);
    const v = {};
    Object.entries(existing.valuesCents).forEach(([k, c]) => {
      v[k] = centsToInput(c);
    });
    setValues(v);
    setFob(centsToInput(existing.fobCents));
    setToast("Loaded existing demo price.");
  };

  const activeBands = useCustom ? customBands : state.globalBands;

  const save = () => {
    const valuesCents = {};
    activeBands.forEach((b) => {
      const raw = values[b.label];
      valuesCents[b.label] =
        raw === "" || raw === undefined ? null : Math.round(Number(raw) * 100);
    });
    const price = {
      id: existing ? existing.id : 0,
      sportId,
      itemId,
      fabricId,
      liningIds: [],
      presetName: useCustom ? matchPresetName(customBands) : null,
      fobCents: fob === "" ? 0 : Math.round(Number(fob) * 100),
      status: existing ? existing.status : "active",
      valuesCents,
    };
    dispatch({ type: "UPSERT_PRICE", price });
    setToast("Saved (demo only). Check the Price Grid and Quote Simulator.");
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Configure a product price
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              size="small"
              label="Sports Category"
              value={sportId}
              onChange={(e) => {
                const s = Number(e.target.value);
                setSportId(s);
                const first = ITEMS.find((i) => i.sportId === s);
                if (first) setItemId(first.id);
              }}
            >
              {SPORTS.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              size="small"
              label="Item"
              value={itemId}
              onChange={(e) => setItemId(Number(e.target.value))}
            >
              {items.map((i) => (
                <MenuItem key={i.id} value={i.id}>
                  {i.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              size="small"
              label="Fabric"
              value={fabricId}
              onChange={(e) => setFabricId(Number(e.target.value))}
            >
              {FABRICS.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <Button size="small" sx={{ mt: 1 }} onClick={loadExisting}>
          Load current demo price for this combo
        </Button>
      </Paper>

      <FormControlLabel
        control={
          <Switch
            checked={useCustom}
            onChange={(e) => setUseCustom(e.target.checked)}
          />
        }
        label="Custom bands for this item"
      />

      {useCustom && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <Typography variant="subtitle2">Custom band set</Typography>
            <TextField
              select
              size="small"
              label="Copy from preset"
              value=""
              sx={{ minWidth: 220 }}
              onChange={(e) => setCustomBands(BAND_PRESETS[e.target.value])}
            >
              {Object.keys(BAND_PRESETS).map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <BandSetEditor bands={customBands} onChange={setCustomBands} />
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Unit pricing {useCustom ? "(this item's bands)" : "(global bands)"}
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#D5F9FC" }}>
              <TableCell sx={{ fontWeight: 600 }}>Quantity band</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Price per unit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeBands.map((b) => (
              <TableRow key={b.label}>
                <TableCell>{b.label}</TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    size="small"
                    variant="standard"
                    placeholder="0.00"
                    value={values[b.label] ?? ""}
                    onChange={(e) =>
                      setValues((v) => ({ ...v, [b.label]: e.target.value }))
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ fontStyle: "italic" }}>
                Factory cost (FOB)
              </TableCell>
              <TableCell>
                <TextField
                  type="number"
                  size="small"
                  variant="standard"
                  placeholder="0.00"
                  value={fob}
                  onChange={(e) => setFob(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Divider sx={{ my: 2 }} />
        <Button variant="contained" color="success" onClick={save}>
          Save to demo
        </Button>
        {existing && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: 2 }}
          >
            Editing demo price #{existing.id} · FOB {formatCents(existing.fobCents)}
          </Typography>
        )}
      </Paper>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast("")}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}

function matchPresetName(bands) {
  const j = JSON.stringify(bands);
  const hit = Object.entries(BAND_PRESETS).find(
    ([, v]) => JSON.stringify(v) === j
  );
  return hit ? hit[0] : "Custom";
}
